import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Eye,
  UserPlus,
  MoreVertical,
  DollarSign,
  MapPin,
  Calendar,
  Trash2,
  CheckCircle,
  XCircle,
  Clock,
} from "lucide-react";
import { LeadDetailsModal } from "./LeadDetailsModal";
import { AssignContractorModal } from "./AssignContractorModal";

interface Lead {
  id: string;
  title: string;
  category: string;
  tradesmens_commission: string;
  description: string;
  budget: number;
  location: {
    address: string;
    city: string;
    state: string;
    zipCode: string;
  };
  lead_sub_cat: string;
  postedDate: string;
  status: string;
  homeowner: {
    id: number;
    name: string;
    email: string;
    phone: string;
    address: string;
    city: string;
    state: string;
    zipCode: string;
    pic: string;
    memberSince: string;
    completedProjects: number;
  };
  images: string[];
  media: Array<{
    type: string;
    url: string;
  }>;
  activities?: Array<{
    id: string;
    type: "status_change" | "assigned" | "note" | "created";
    description: string;
    timestamp: string;
    user: string;
  }>;
}

interface LeadsTableProps {
  leads: Lead[];
  onUpdateLead: (
    leadId: string,
    updates: Partial<Lead>,
  ) => void;
  onDeleteLead: (leadId: string) => void;
  setRefresh:any

}

export function LeadsTable({
  leads,
  onUpdateLead,
  onDeleteLead,setRefresh
}: LeadsTableProps) {
  const [selectedLead, setSelectedLead] = useState<Lead | null>(
    null,
  );
  const [isDetailsModalOpen, setIsDetailsModalOpen] =
    useState(false);
  const [isAssignModalOpen, setIsAssignModalOpen] =
    useState(false);
  const [assigningLeadId, setAssigningLeadId] = useState<
    string | null
  >(null);
  const [activeDropdown, setActiveDropdown] = useState<
    string | null
  >(null);

  const [searchParams, setSearchParams] = useSearchParams();
  const requestedLeadId = searchParams.get("leadId");
  useEffect(() => {
    if (!requestedLeadId) return;
    const lead = leads.find(item => String(item.id) === requestedLeadId);
    if (lead) {
      setSelectedLead(lead);
      setIsDetailsModalOpen(true);
    }
  }, [requestedLeadId, leads]);

  const closeDetails = () => {
    setIsDetailsModalOpen(false);
    if (requestedLeadId) {
      const next = new URLSearchParams(searchParams);
      next.delete("leadId");
      setSearchParams(next, { replace: true });
    }
  };

  const handleViewDetails = (lead: Lead) => {
    setSelectedLead(lead);
    setIsDetailsModalOpen(true);
    setActiveDropdown(null);
  };

  const handleAssignClick = (leadId: string) => {
    setAssigningLeadId(leadId);
    setIsAssignModalOpen(true);
    setActiveDropdown(null);
  };

  const handleAssignContractor = (
    leadId: string,
    contractorId: string,
  ) => {
    console.log(
      `Assigning contractor ${contractorId} to lead ${leadId}`,
    );
  setRefresh((r:any)=>!r)
  };

  const handleStatusChange = (
    leadId: string,
    newStatus: string,
  ) => {
    onUpdateLead(leadId, { status: newStatus });
    setActiveDropdown(null);
  };

  const handleDelete = (leadId: string) => {
    if (
      window.confirm(
        "Are you sure you want to delete this lead?",
      )
    ) {
      onDeleteLead(leadId);
      setActiveDropdown(null);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case "declined":
        return "bg-red-100 text-red-700";
      case "accepted":
        return "bg-green-100 text-green-700";
      case "pending":
        return "bg-yellow-100 text-yellow-700";
      case "open":
        return "bg-blue-100 text-blue-700";
      case "completed":
        return "bg-purple-100 text-purple-700";
      default:
        return "bg-gray-100 text-gray-700";
    }
  };

  return (
    <>
      <div className="overflow-x-auto all-leads-table">
        <table className="w-full">
          <thead>
            <tr className="border-b bg-gray-50">
              <th className="text-left px-3 py-3 text-xs text-gray-600 uppercase">
                ID
              </th>
              <th className="text-left px-3 py-3 text-xs text-gray-600 uppercase">
                Title
              </th>
              <th className="text-left px-3 py-3 text-xs text-gray-600 uppercase">
                Homeowner
              </th>
              <th className="text-left px-3 py-3 text-xs text-gray-600 uppercase">
                Location
              </th>
              <th className="text-left px-3 py-3 text-xs text-gray-600 uppercase">
                Budget / Commission
              </th>
              <th className="text-left px-3 py-3 text-xs text-gray-600 uppercase">
                Posted
              </th>
              <th className="text-left px-3 py-3 text-xs text-gray-600 uppercase">
                Status
              </th>
              <th className="text-left px-3 py-3 text-xs text-gray-600 uppercase">
                Actions
              </th>
            </tr>
          </thead>
          <tbody>
            {leads.map((lead) => (
              <tr
                key={`${lead.id}-${lead.homeowner.id}`}
                className="border-b border-b-gray-200 hover:bg-gray-50 transition-colors"
              >
                <td className="px-2 py-3">
                  <span className="text-sm font-medium text-gray-900">
                    #{lead.id}
                  </span>
                </td>
                <td className="px-3 py-3">
                  <div>
                    <p className="text-sm text-gray-900 font-medium">
                      {lead.title}
                    </p>
                    <p className="text-xs text-gray-500">
                      {lead.category}
                    </p>
                  </div>
                </td>
                <td className="px-3 py-3">
                  <div>
                    <p className="text-sm text-gray-900">
                      {lead.homeowner.name}
                    </p>
                    <p className="text-xs text-gray-500">
                      {lead.homeowner.phone}
                    </p>
                  </div>
                </td>
                <td className="px-3 py-3">
                  <div className="flex items-start gap-1">
                    <MapPin className="w-3 h-3 text-gray-400 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-xs text-gray-700">
                        {lead.location.city},{" "}
                        {lead.location.state}
                      </p>
                    </div>
                  </div>
                </td>
                <td className="px-3 py-3">
                  <div>
                    <div className="flex items-center gap-1 text-gray-900">
                      <DollarSign className="w-3 h-3 text-gray-400" />
                      <span className="text-sm font-medium">
                        {lead.budget.toLocaleString()}
                      </span>
                    </div>
                    <div className="flex items-center gap-1 text-green-600">
                      <span className="text-xs">
                        Commission: $
                        {lead.tradesmens_commission}
                      </span>
                    </div>
                  </div>
                </td>
                <td className="px-3 py-3">
                  <div className="flex items-center gap-1 text-xs text-gray-600">
                    <Calendar className="w-3 h-3 text-gray-400" />
                    {formatDate(lead.postedDate)}
                  </div>
                </td>
                <td className="px-3 py-3">
                  <span
                    className={`px-2 py-1 rounded-md text-xs font-medium ${getStatusColor(lead.status)}`}
                  >
                    {lead.status.charAt(0).toUpperCase() +
                      lead.status.slice(1)}
                  </span>
                </td>
                <td className="px-3 py-3">
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => handleViewDetails(lead)}
                      className="p-1.5 border-0 bg-transparent text-blue-600 hover:bg-blue-50 rounded-md transition-colors"
                      title="View Details"
                    >
                      <Eye className="w-4 h-4" />
                    </button>
                    {lead.status ==="open" &&    <button
                      onClick={() => handleAssignClick(lead.id)}
                      className="p-1.5 border-0 bg-transparent text-green-600 hover:bg-green-50 rounded-md transition-colors"
                      title="Assign Contractor"
                    >
                      <UserPlus className="w-4 h-4" />
                    </button>}
                    <button
                      onClick={() => handleDelete(lead.id)}
                      className="p-1.5 border-0 bg-transparent text-red-600 hover:bg-red-50 rounded-md transition-colors"
                      title="Delete Lead"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                 
                    {/* <div className="relative">
                      <button
                        onClick={() =>
                          setActiveDropdown(
                            activeDropdown === lead.id
                              ? null
                              : lead.id,
                          )
                        }
                        className="p-1.5 border-0 bg-transparent text-gray-600 hover:bg-gray-100 rounded-md transition-colors"
                      >
                        <MoreVertical className="w-4 h-4" />
                      </button>
                      {activeDropdown === lead.id && (
                        <>
                          <div
                            className="fixed inset-0 z-10"
                            onClick={() =>
                              setActiveDropdown(null)
                            }
                          ></div>
                          <div className="absolute right-0 mt-1 w-44 bg-white rounded-lg shadow-lg border border-gray-200 z-20">
                            <div className="py-1">
                              <p className="px-3 py-2 text-xs text-gray-500 uppercase font-medium">
                                Change Status
                              </p>
                              <button
                                onClick={() =>
                                  handleStatusChange(
                                    lead.id,
                                    "active",
                                  )
                                }
                                className="w-full border-0 bg-transparent text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2 transition-colors"
                              >
                                <CheckCircle className="w-4 h-4 text-green-600" />
                                Active
                              </button>
                              <button
                                onClick={() =>
                                  handleStatusChange(
                                    lead.id,
                                    "pending",
                                  )
                                }
                                className="w-full border-0 bg-transparent text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2 transition-colors"
                              >
                                <Clock className="w-4 h-4 text-amber-600" />
                                Pending
                              </button>
                              <button
                                onClick={() =>
                                  handleStatusChange(
                                    lead.id,
                                    "declined",
                                  )
                                }
                                className="w-full border-0 bg-transparent text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2 transition-colors"
                              >
                                <XCircle className="w-4 h-4 text-red-600" />
                                Declined
                              </button>
                              <button
                                onClick={() =>
                                  handleStatusChange(
                                    lead.id,
                                    "completed",
                                  )
                                }
                                className="w-full border-0 bg-transparent text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2 transition-colors"
                              >
                                <CheckCircle className="w-4 h-4 text-purple-600" />
                                Completed
                              </button>
                              <div className="border-t my-1"></div>
                              <button
                                onClick={() =>
                                  handleDelete(lead.id)
                                }
                                className="w-full border-0 bg-transparent text-left px-3 py-2 text-sm text-red-600 hover:bg-red-50 flex items-center gap-2 transition-colors"
                              >
                                <Trash2 className="w-4 h-4" />
                                Delete Lead
                              </button>
                            </div>
                          </div>
                        </>
                      )}
                    </div> */}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <LeadDetailsModal
        lead={selectedLead}
        isOpen={isDetailsModalOpen}
        onClose={closeDetails}
      />

      <AssignContractorModal
        leadId={assigningLeadId || ""}
        isOpen={isAssignModalOpen}
        onClose={() => setIsAssignModalOpen(false)}
        onAssign={handleAssignContractor}
      />
    </>
  );
}
